import React, { useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Flex, Grid, GridItem, Spinner, Text } from '@chakra-ui/react'
import { Icon, Button } from '@ubirider/pick-component-library'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'

import BalanceIcon from '../../../../assets/dollar.svg'
import TripsIcon from '../../../../assets/operation.svg'
import LeafIcon from '../../../../assets/leaf.svg'
import CardIcon from '../../../../assets/credit_card.svg'
import ChevronRight from '../../../../assets/chevron_right.svg'

import { useCards, useTrips, useTripsFiltered } from '../../../../context/DataProvider'

const CO2_PER_EURO_KG = 0.005

const MFlex = motion.create(Flex)
const MGrid = motion.create(Grid)

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { when: 'beforeChildren', staggerChildren: 0.08, delayChildren: 0.05 },
  },
}

const cardVariants = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring', stiffness: 360, damping: 26 },
  },
}

export const InfoSection = ({ onEnterComplete }) => {
  const { t } = useTranslation()
  const { pickCard, loading: cardsLoading } = useCards()
  const { trips: sortedTrips, loading: tripsLoading } = useTripsFiltered()
  const loading = cardsLoading || tripsLoading

  const { amountSpent, co2Saved } = useMemo(() => {
    if (!sortedTrips.length) return { amountSpent: '0.00', co2Saved: 0 }
    const totalCents = sortedTrips.reduce((sum, item) => {
      const n = Number(item?.amount)
      return sum + (Number.isFinite(n) ? Math.round(n * 100) : 0)
    }, 0)
    const euros = totalCents / 100
    return {
      amountSpent: euros.toFixed(2),
      co2Saved: Number((euros * CO2_PER_EURO_KG).toFixed(2)),
    }
  }, [sortedTrips])

  // Fire once after loading completes & animations finish
  const firedRef = useRef(false)
  useEffect(() => {
    if (loading) firedRef.current = false
  }, [loading])
  const fireOnce = () => {
    if (firedRef.current) return
    firedRef.current = true
    onEnterComplete?.()
  }

  if (loading) {
    return (
      <Flex minH='240px' bg='neutral.100' align='center' justify='center' borderRadius='8px'>
        <Spinner thickness='6px' color='neutral.500' size='xl' />
      </Flex>
    )
  }

  return (
    <MFlex
      gap={{ sm: '24px', md: '16px', lg: '16px', xl: '16px' }}
      direction='column'
      width='100%'
      initial='hidden'
      animate='visible'
      variants={containerVariants}
      onAnimationComplete={fireOnce}
    >
      {/* Desktop / tablet */}
      <MFlex
        direction='column'
        overflow='hidden'
        align='center'
        display={{ sm: 'none', md: 'flex' }}
        variants={containerVariants}
      >
        <MGrid
          variants={containerVariants}
          templateColumns={{
            sm: 'repeat(1, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(3, 1fr)',
            xxl: 'repeat(3, 1fr)',
          }}
          gap='20px'
          w='100%'
        >
          {/* First card spans full row */}
          {pickCard && pickCard.status === 'active' && (
            <GridItem colSpan={{ sm: 1, md: 3 }}>
              <MFlex
                direction='column'
                bg='secondary.600'
                borderRadius='8px'
                padding='24px'
                as={Link}
                to='/wallet/pick-card'
                role='link'
                tabIndex={0}
                cursor='pointer'
                _hover={{ filter: 'brightness(1.05)' }}
                _active={{ filter: 'brightness(0.98)' }}
                variants={cardVariants}
              >
                <Flex justify='space-between' align='center'>
                  <Flex h='36px' gap='4px' align='center'>
                    <Icon src={CardIcon} fill='white' />
                    <Text fontSize='18px' fontWeight='600' color='white'>
                      {t('demo.current_balance')}
                    </Text>
                  </Flex>
                  <Button
                    style='typo'
                    label={t('demo.card_details')}
                    size='small'
                    labelCss={{ color: 'white !important' }}
                    rightIconProps={{ src: ChevronRight, fill: 'white !important' }}
                  />
                </Flex>
                <Text fontSize='30px' fontWeight='700' color='white'>
                  {(pickCard?.balance ?? 0).toFixed(2)}€
                </Text>
              </MFlex>
            </GridItem>
          )}

          {/* Next three cards share the second row */}
          <MFlex direction='column' bg='neutral.100' borderRadius='8px' padding='24px' variants={cardVariants}>
            <Flex h='36px' gap='4px' align='center'>
              <Icon src={BalanceIcon} />
              <Text fontSize='18px' fontWeight='600' color='neutral.500'>
                {t('demo.amount_spent')}
              </Text>
            </Flex>
            <Text fontSize='30px' fontWeight='700'>{amountSpent}€</Text>
          </MFlex>

          <MFlex direction='column' bg='neutral.100' borderRadius='8px' padding='24px' variants={cardVariants}>
            <Flex justify='space-between' align='center'>
              <Flex h='36px' gap='4px' align='center'>
                <Icon src={TripsIcon} fill='$neutral500' />
                <Text fontSize='18px' fontWeight='600' color='neutral.500'>
                  {t('demo.no_trips')}
                </Text>
              </Flex>
            </Flex>
            <Text fontSize='30px' fontWeight='700'>{sortedTrips.length}</Text>
          </MFlex>

          <MFlex direction='column' bg='neutral.100' borderRadius='8px' padding='24px' variants={cardVariants}>
            <Flex h='36px' gap='4px' align='center'>
              <Icon src={LeafIcon} />
              <Text fontSize='18px' fontWeight='600' color='neutral.500'>
                {t('demo.co2_saved')}
              </Text>
            </Flex>
            <Text fontSize='30px' fontWeight='700'>{co2Saved}kg</Text>
          </MFlex>
        </MGrid>
      </MFlex>

      {/* Mobile */}
      <MFlex
        direction='column'
        overflow='hidden'
        align='center'
        justify='space-between'
        mr='-24px'
        ml='-24px'
        display={{ sm: 'flex', md: 'none' }}
        variants={containerVariants}
      >
        <MGrid
          variants={containerVariants}
          templateColumns={{
            sm: 'repeat(1, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(3, 1fr)',
            xxl: 'repeat(3, 1fr)',
          }}
          gap='16px'
          overflowX='scroll'
          w='100%'
          pb='16px'
          pl='16px'
          sx={{ '::-webkit-scrollbar': { display: 'none' } }}
        >
          {pickCard && pickCard.status === 'active' && (
            <MFlex
              direction='column'
              bg='secondary.600'
              borderRadius='8px'
              padding='24px'
              as={Link}
              to='/wallet/pick-card'
              role='link'
              tabIndex={0}
              cursor='pointer'
              _hover={{ filter: 'brightness(1.05)' }}
              _active={{ filter: 'brightness(0.98)' }}
              variants={cardVariants}
            >
              <Flex gap='4px' align='center'>
                <Icon src={CardIcon} fill='white' />
                <Text fontSize='18px' fontWeight='600' color='white'>
                  {t('demo.current_balance')}
                </Text>
              </Flex>
              <Text fontSize='30px' fontWeight='700' color='white'>
                {(pickCard?.balance ?? 0).toFixed(2)}€
              </Text>
            </MFlex>
          )}

          <MFlex direction='column' bg='neutral.100' borderRadius='8px' padding='24px' variants={cardVariants}>
            <Flex gap='4px' align='center'>
              <Icon src={BalanceIcon} />
              <Text fontSize='18px' fontWeight='600' color='neutral.500'>
                {t('demo.amount_spent')}
              </Text>
            </Flex>
            <Text fontSize='30px' fontWeight='700'>{amountSpent}€</Text>
          </MFlex>

          <MFlex direction='column' bg='neutral.100' borderRadius='8px' padding='24px' variants={cardVariants}>
            <Flex gap='4px' align='center'>
              <Icon src={TripsIcon} fill='$neutral500' />
              <Text fontSize='18px' fontWeight='600' color='neutral.500'>
                {t('demo.no_trips')}
              </Text>
            </Flex>
            <Text fontSize='30px' fontWeight='700'>{sortedTrips.length}</Text>
          </MFlex>

          <MFlex direction='column' bg='neutral.100' borderRadius='8px' padding='24px' variants={cardVariants}>
            <Flex gap='4px' align='center'>
              <Icon src={LeafIcon} />
              <Text fontSize='18px' fontWeight='600' color='neutral.500'>
                {t('demo.co2_saved')}
              </Text>
            </Flex>
            <Text fontSize='30px' fontWeight='700'>{co2Saved}kg</Text>
          </MFlex>
        </MGrid>
      </MFlex>
    </MFlex>
  )
}